import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Loader2, AlertTriangle, UserX } from 'lucide-react';
import { toast } from 'sonner';
import { adminAPI } from '../../services/api';

export default function DeleteCounselorDialog({ open, onOpenChange, counselor, onSuccess }) {
  const [loading, setLoading] = useState(false);

  const handleDelete = async (permanent = false) => {
    setLoading(true);

    try {
      const result = await adminAPI.deleteCounselor(counselor._id, permanent);

      if (permanent) {
        toast.success('Counselor permanently deleted');
      } else {
        toast.success('Counselor deactivated successfully');
        if (result.activeAssignments > 0) {
          toast.info(`Note: ${result.activeAssignments} active assignment(s) still exist`);
        }
      }

      onOpenChange(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete counselor';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const counselorName = counselor
    ? `${counselor.firstName} ${counselor.lastName}`
    : 'this counselor';

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <UserX className="h-6 w-6 text-red-600" />
            </div>
            <AlertDialogTitle>Delete Counselor</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="space-y-3">
            <p>
              Are you sure you want to remove <strong>{counselorName}</strong>?
            </p>

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <div className="flex gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">What happens when you deactivate:</p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>The counselor will not be able to log in</li>
                    <li>Their assigned students will remain assigned</li>
                    <li>Their history and data will be preserved</li>
                    <li>You can reactivate them later</li>
                  </ul>
                </div>
              </div>
            </div>

            {counselor?.activeAssignments > 0 && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <p className="text-sm text-blue-800">
                  This counselor has <strong>{counselor.activeAssignments} active assignment(s)</strong>.
                  Consider reassigning students before removing.
                </p>
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => handleDelete(false)}
            disabled={loading}
            className="w-full sm:w-auto"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              'Deactivate Counselor'
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
